import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, BookOpen, FunctionSquare, BarChart2, Calendar, UploadCloud, HelpCircle } from 'lucide-react';
import { HowItWorksModal } from './HowItWorksModal';
import { NixlabsAccountWidget } from './NixlabsAccountWidget';

export const TopBar: React.FC = () => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
      isActive
        ? 'bg-paper-200 text-zinc-900 dark:bg-study-card dark:text-zinc-100 border border-paper-300 dark:border-study-line font-semibold'
        : 'text-zinc-600 hover:text-zinc-900 hover:bg-paper-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-study-panel'
    }`;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-paper-300 bg-paper-50/95 backdrop-blur dark:border-study-line dark:bg-study-slate/95">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-8">
          <div
            onClick={() => navigate('/')}
            className="flex cursor-pointer select-none items-baseline gap-1 font-mono text-sm tracking-tight"
          >
            <span style={{ color: '#38bdf8' }}>vestibular</span>
            <span style={{ color: '#888' }}>.nixlabs.tech</span>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" className={navItemClass}>
              <Home className="h-3.5 w-3.5" />
              Início
            </NavLink>
            <NavLink to="/triagem" className={navItemClass}>
              <BookOpen className="h-3.5 w-3.5" />
              Triagem
            </NavLink>
            <NavLink to="/grafo" className={navItemClass}>
              <FunctionSquare className="h-3.5 w-3.5" />
              Fórmulas
            </NavLink>
            <NavLink to="/dashboard" className={navItemClass}>
              <BarChart2 className="h-3.5 w-3.5" />
              Falhas
            </NavLink>
            <NavLink to="/calendario" className={navItemClass}>
              <Calendar className="h-3.5 w-3.5" />
              Calendário
            </NavLink>
            <NavLink to="/upload" className={navItemClass}>
              <UploadCloud className="h-3.5 w-3.5" />
              Provas PDF
            </NavLink>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {/* Circular Question Mark Button with Pointer-Following Pill */}
          <button
            type="button"
            onClick={() => {
              setTooltipVisible(false);
              setHelpOpen(true);
            }}
            onMouseEnter={(e) => {
              setTooltipVisible(true);
              setTooltipPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseMove={(e) => {
              setTooltipPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={() => setTooltipVisible(false)}
            aria-label="Como funciona"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-paper-300 bg-paper-100 text-xs font-bold text-zinc-700 transition hover:bg-paper-200 hover:text-zinc-900 active:scale-95 dark:border-study-line dark:bg-study-panel dark:text-zinc-300 dark:hover:bg-study-card dark:hover:text-zinc-100 shadow-sm"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <NixlabsAccountWidget />
        </div>
      </div>

      {/* Pointer-Following Tooltip Pill */}
      {tooltipVisible && (
        <div
          className="pointer-events-none fixed z-50 rounded-full border border-paper-300 bg-zinc-900 px-3 py-1 font-sans text-[11px] font-semibold tracking-wide text-white shadow-xl dark:border-study-line dark:bg-zinc-100 dark:text-zinc-900 transition-transform"
          style={{
            left: `${tooltipPos.x + 12}px`,
            top: `${tooltipPos.y + 12}px`,
          }}
        >
          Como funciona
        </div>
      )}

      {/* How It Works Scrollable Modal */}
      <HowItWorksModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
};
